import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, DialogActions, DialogContent } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { TEventFormData, TFormType } from 'src/@types';
import { useDialogContext } from 'src/components/dialog/DialogContext';
import CEditor from 'src/components/editor/CEditor';
import FormProvider, { RHFSelect, RHFTextField } from 'src/components/hook-form';
import RHFDatePicker from 'src/components/hook-form/RHFDatePicker';
import useGetSelectOption from 'src/hooks/useGetSelectOption';
import { FormInnerContainer, FormItem, FormLabel, FormRow } from 'src/layouts/form/FormLayout';
import { IEvent, useStores } from 'src/models';
import { eventApi } from 'src/service';
import { useHandleResponse } from 'src/service/useHandleResponse';
import { FormType } from 'src/static/constants/common/form';
import { getEventFormDefaultValues } from 'src/utils/getRHFDefaultValues';

interface IEventFormDialogProps {
  formType: TFormType;
  onSubmitSuccess: () => void;
  selectedData?: IEvent;
}

export const EventFormDialog = observer(
  ({ formType, onSubmitSuccess, selectedData }: IEventFormDialogProps) => {
    const { t } = useTranslation();
    const handleResponse = useHandleResponse();
    const { alertStore } = useStores();
    const { formId } = useDialogContext();
    const readonly = formType === FormType.READONLY;
    const isCreate = formType === FormType.CREATE;
    const { getEventCategory } = useGetSelectOption();

    const methods = useForm<TEventFormData>({
      mode: 'all',
      reValidateMode: 'onChange',
      defaultValues: getEventFormDefaultValues(selectedData),
    });

    // 등록 / 수정 함수
    const onValid = async (data: TEventFormData) => {
      const formData = {
        ...(!isCreate && { eventSid: selectedData?.eventSid }),
        ...data,
      };
      const result = isCreate
        ? eventApi.post(formData)
        : eventApi.put(selectedData?.eventSid || 0, formData);
      if (await result) {
        handleResponse('등록이 완료되었습니다.', '등록이 실패되었습니다.', onSubmitSuccess);
      }
    };

    // 삭제
    const handleDelete = () => {
      alertStore.setOnConfirm(async () => {
        if (!selectedData?.eventSid) return;
        eventApi
          .remove(selectedData?.eventSid)
          .then(() =>
            handleResponse('삭제가 완료되었습니다.', '삭제가 실패되었습니다.', onSubmitSuccess),
          );
      });
      alertStore.setProps({
        open: true,
        title: '스케줄 삭제',
        content: '해당 스케줄을 삭제하시겠습니까?',
        category: 'error',
        hasCancelButton: true,
      });
    };
    return (
      <FormProvider methods={methods} onSubmit={methods.handleSubmit(onValid)} formId={formId}>
        <DialogContent dividers sx={{ pt: '10px' }}>
          <FormInnerContainer>
            <FormLabel>{t('common.form.section.basicInfo')}</FormLabel>
            <FormRow sx={{ gridTemplateColumns: '1fr 1fr' }}>
              <FormItem>
                <RHFSelect
                  label={t('event.form.type')}
                  name="eventTypeCd"
                  options={getEventCategory()}
                  useAll={false}
                />
              </FormItem>
              <FormItem>
                <RHFDatePicker label={t('event.form.startDt')} name="startDt" />
              </FormItem>
            </FormRow>
            <FormRow sx={{ gridTemplateColumns: '1fr' }}>
              <FormItem>
                <RHFTextField
                  label={t('event.form.location')}
                  name="location"
                  disabled={readonly}
                />
              </FormItem>
            </FormRow>
            <FormRow sx={{ gridTemplateColumns: '1fr' }}>
              <FormItem>
                <RHFTextField label={t('event.form.title')} name="title" disabled={readonly} />
              </FormItem>
            </FormRow>
          </FormInnerContainer>
          <FormInnerContainer>
            <FormLabel>{t('common.form.section.content')}</FormLabel>
            <FormRow sx={{ gridTemplateColumns: '1fr' }}>
              <FormItem>
                <CEditor name={'contents'} />
              </FormItem>
            </FormRow>
          </FormInnerContainer>
        </DialogContent>
        <DialogActions>
          <>
            {!isCreate && (
              <Button onClick={handleDelete} variant="contained" color="error">
                {t('common.button.delete')}
              </Button>
            )}
            <Button type="submit" variant="contained" form={formId} color="primary">
              {isCreate ? t('common.button.register') : t('common.button.edit')}
            </Button>
          </>
        </DialogActions>
      </FormProvider>
    );
  },
);
